import { gql, useQuery } from '@apollo/client';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../providers/userContextProvider';
import { Skeleton } from '../../1-primative';
import {
  Card, Pagination, Table, TableCell, TableHeadCell, TableRow,
} from '../../2-component';
import { generateClientNameString } from '../../../util';
import { useLocalization } from '../../../util/useLocalization';
import { usePageState } from '../../../util/usePageState';
import { AccountReconciliationSnapshot } from '../../../interfaces';

const FETCH_ACCOUNT_RECONCILIATION_SNAPSHOTS = gql`
  query FetchAccountReconciliationSnapshots($input: FetchAccountReconciliationSnapshotsInput!) {
    fetchAccountReconciliationSnapshots(input: $input) {
      accountReconciliationSnapshots {
        id
        date
        account {
          id custodianAccountNumber type
          user { id firstName lastName entityName organization { id name } }
        }
        custodianUpdatedAt
        oneVestUpdatedAt
        flags {
          type
        }
      }
      totalCount
    }
  }
`;
export const ReconciliationV2 = () => {
  const { activeOrganization } = useContext(UserContext);
  const { t } = useTranslation(['reconciliation', 'accountTypes']);
  const navigate = useNavigate();
  const { localizedDate, localizedDateTime } = useLocalization();
  const [page, setPage] = usePageState(1, 'page');
  const [pageSize, setPageSize] = usePageState(15, 'pageSize');
  const { data, loading, previousData } = useQuery(FETCH_ACCOUNT_RECONCILIATION_SNAPSHOTS, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id,
          isFlagged: true,
        },
        pagination: {
          sortField: 'date',
          sortDesc: true,
          perPage: pageSize,
          offSet: (page - 1) * pageSize,
        },
      },
    },
  });

  return (
    <Card>
      <Table>
        <TableRow>
          <TableHeadCell>{t('table.account')}</TableHeadCell>
          <TableHeadCell>{t('table.date')}</TableHeadCell>
          <TableHeadCell>{t('table.flags')}</TableHeadCell>
          <TableHeadCell>{t('table.systemUpdated')}</TableHeadCell>
          <TableHeadCell>{t('table.externalUpdated')}</TableHeadCell>
          <TableHeadCell>{t('table.organization')}</TableHeadCell>
        </TableRow>
        {loading && !previousData && [...Array(pageSize)].map((_, i) => (
          <TableRow key={i}>
            <TableCell><Skeleton width="100%" /></TableCell>
            <TableCell><Skeleton width="100%" /></TableCell>
            <TableCell><Skeleton width="100%" /></TableCell>
            <TableCell><Skeleton width="100%" /></TableCell>
            <TableCell><Skeleton width="100%" /></TableCell>
            <TableCell><Skeleton width="100%" /></TableCell>
          </TableRow>
        ))}
        {(data || previousData)?.fetchAccountReconciliationSnapshots.accountReconciliationSnapshots.map((doc: AccountReconciliationSnapshot) => (
          <TableRow onClick={() => navigate(`${doc.id}`)} hover pointer>
            <TableCell>{generateClientNameString(doc?.account?.user, false, false)} - {t(`accountTypes:${doc.account.type}`)}</TableCell>
            <TableCell>{localizedDate(doc.date)}</TableCell>
            <TableCell>{doc.flags.length}</TableCell>
            <TableCell>{localizedDateTime(doc.oneVestUpdatedAt)}</TableCell>
            <TableCell>{localizedDateTime(doc.custodianUpdatedAt)}</TableCell>
            <TableCell>{doc.account.user.organization.name}</TableCell>
          </TableRow>
        ))}
      </Table>
      <Pagination
        count={Math.ceil(((data || previousData)?.fetchAccountReconciliationSnapshots?.totalCount ?? 0) / pageSize)}
        page={page}
        perPage={pageSize}
        onChangePerPage={(newPageSize) => setPageSize(newPageSize)}
        onChange={(_e, newPage) => setPage(newPage)}
        sx={{
          p: 1,
          textAlign: 'right',
          '.MuiPagination-ul': {
            justifyContent: 'end',
          },
        }}
      />
    </Card>
  );
};
